<script setup lang="ts">
import { useModyApi } from "~/middelware/ModyApi";
import {onMounted} from "@vue/runtime-core";
import type {Category} from "~/utils/Category";
import type {MenuItem} from "primevue/menuitem";
import {useRoute, useRouter} from "vue-router";


const api = useModyApi();
const props = withDefaults(defineProps<{ showTitle?: boolean }>(), { showTitle: true })
const menuItems = ref<MenuItem[]|undefined>([])
const categories = ref<Category[]>([])
const router = useRouter();
const route = useRoute();

function convertCategoriesToMenuItems(categories:Category[]):MenuItem[]|undefined{
  let nodes:MenuItem[] = []
  categories.forEach((category:Category)=>{
    if(category.products.length == 0 && category.subCategories.length == 0){
      return;
    }
    const newNode:MenuItem = {
      key: String(category.id),
      label:category.name,
      command: ()=>{router.push("/shop/category/"+category.slug)},
      items: convertCategoriesToMenuItems(category.subCategories),
      class: route.params.slug === category.slug ? "is-current-category" : undefined,
    }
    if(newNode.items) newNode.command = undefined
    nodes.push(newNode)
  })
  if(nodes.length == 0) return undefined
  return nodes
}

onMounted(async ()=>{
  categories.value = await api.getCategories();
  menuItems.value = convertCategoriesToMenuItems(categories.value)
})

watch(() => route.params.slug, () => {
  menuItems.value = convertCategoriesToMenuItems(categories.value)
})

</script>
<template>
    <div class="menuContainer">
      <div v-if="props.showTitle" class="tree-title">
        <span>Sortiment</span>
        <small>Alle Bereiche</small>
      </div>
      <div class="skeletons" v-if="!menuItems?.length">
        <Skeleton v-for="width in ['72%', '88%', '65%', '82%', '76%']" :key="width" :width="width" height="2.6rem"></Skeleton>
      </div>
      <PanelMenu v-else :model="menuItems" class="category-tree"></PanelMenu>
    </div>
</template>
<style lang="scss" scoped>

.menuContainer{
  padding: 0;

  @include desktop(){
    max-width: $desktop-content-width;
    margin: 0;
    padding: 0;
  }
}

.tree-title{
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.85rem;

  span{
    color: white;
    font-size: 1.15rem;
    font-weight: 720;
  }

  small{
    color: rgb(120 117 115);
    font-size: 0.7rem;
  }
}

.skeletons{
  display: grid;
  gap: 0.45rem;

  .p-skeleton{
    border-radius: 10px;
  }
}

:deep(.p-panelmenu){
  display: grid;
  gap: 0.35rem;
}

:deep(.p-panelmenu-panel){
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.025);
}

:deep(.p-panelmenu-header-content){
  color: rgb(224 221 218);
  background: transparent;
  border: none;
  border-radius: 0;
  transition: color 150ms ease, background 150ms ease;

  &:hover{
    color: white;
    background: rgba(255, 255, 255, 0.055);
  }
}

:deep(.p-panelmenu-header-link){
  min-height: 44px;
  padding: 0.65rem 0.8rem;
  gap: 0.65rem;
  font-size: 0.84rem;
  font-weight: 650;
}

:deep(.p-panelmenu-header-icon){
  color: #E39737;
  font-size: 0.72rem;
}

:deep(.p-panelmenu-content){
  padding: 0.25rem 0.4rem 0.5rem;
  background: rgba(0, 0, 0, 0.16);
  border: none;
}

:deep(.p-panelmenu-item-link){
  min-height: 38px;
  padding: 0.55rem 0.7rem;
  border-radius: 8px;
  color: rgb(168 162 158);
  font-size: 0.8rem;
  transition: color 150ms ease, background 150ms ease;

  &:hover{
    color: white;
    background: rgba(227, 151, 55, 0.09);
  }
}

:deep(.p-panelmenu-item-link:focus-visible),
:deep(.p-panelmenu-header-link:focus-visible){
  outline: 2px solid #E39737;
  outline-offset: -2px;
}

:deep(.is-current-category > .p-panelmenu-header-content),
:deep(.is-current-category > .p-panelmenu-item-content){
  color: white;
  background: rgba(227, 151, 55, 0.13);
  box-shadow: inset 3px 0 0 #E39737;
}

</style>
